import { useRef, ReactNode, CSSProperties } from "react"
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import icons
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
// import context
import { useScreenSize } from "../Context";
// import global var
import { maxWidthScreen } from "../GlobalVar";

interface FrameProps {
    children: ReactNode;
};
interface FrameStyleProps extends FrameProps {
    style?: CSSProperties;
    smallerPadding?: boolean; 
    pt?: number; 
    pb?: number;
    id?: string;
};


export function MainFrame({ children, style, smallerPadding, pt, pb, id} : FrameStyleProps) {

    const { screenSize } = useScreenSize();
    const isSmall = screenSize === "Small";

    const paddingTop =
     pt !== undefined
      ? pt
      : smallerPadding && isSmall
      ? 64
      : 128;

  const paddingBottom =
     pb !== undefined
      ? pb
      : smallerPadding && isSmall
      ? 64
      : 128;

    return (
        <div className="w-full h-fit overflow-x-hidden" style={style} id={id}>
            <div className="m-auto" style={{ maxWidth: `${maxWidthScreen}px` }}>
                <div
                className="w-full h-fit px-8 lg:px-0 lg:py-32"
                style={{
                    paddingTop: `${paddingTop}px`,
                    paddingBottom: `${paddingBottom}px`,
                }}
                >
                    { children }
                </div>
            </div>
        </div>
    )
};

interface ImageBannerFrameProps extends FrameProps {
    image: string;
    height?: number;
    /** Skip slide-in motion — use on 404 where content should appear with the background immediately */
    disableContentMotion?: boolean;
};
export function ImageBannerFrame({
    children,
    image,
    height,
    disableContentMotion = false,
}: ImageBannerFrameProps) {
    const location = useLocation();

    const inner = (
        <div
            className="relative h-full w-full m-auto flex content-center"
            style={{ maxWidth: `${maxWidthScreen}px` }}
        >
            {children}
        </div>
    );

    return (
        <div
            className="relative w-full h-screen overflow-hidden bg-black bg-cover bg-center"
            style={{ backgroundImage: `url(${image})`, maxHeight: height }}
        >
            <div className="relative h-full w-full backdrop-brightness-50">
                {disableContentMotion ? (
                    inner
                ) : (
                    <motion.div
                        className="relative h-full w-full m-auto flex content-center"
                        style={{ maxWidth: `${maxWidthScreen}px` }}
                        key={location.pathname}
                        initial={{ y: "20%" }}
                        animate={{ y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        {children}
                    </motion.div>
                )}
            </div>
        </div>
    );
};


export function HorizontalScrollFrame({ children } : FrameProps) {
    const { screenSize } = useScreenSize();

    let style = screenSize === "Large"
                ? "w-inherit h-fit grid grid-flow-col auto-cols-auto gap-8 p-8 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                : "flex items-center justify-center flex-col gap-8 pt-16"
    return (
        <div className={style}>
            { children }
        </div>
    )
};

interface HorizontalScrollArrowsFrameProps extends FrameProps {
    arrowStyle?: CSSProperties;
};
export function HorizontalScrollArrowsFrame({ children, arrowStyle } : HorizontalScrollArrowsFrameProps) {
    const { screenSize } = useScreenSize();

    let style = screenSize === "Large"
                ? "w-inherit h-fit grid grid-flow-col auto-cols-auto gap-8 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                : "flex items-center justify-center flex-col gap-8 py-10"

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // scroll functions
    function scrollLeft() {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' }); // Adjust scroll distance as needed
        }
    };
    function scrollRight() {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' }); // Adjust scroll distance as needed
        }
    };

    let scrollDisplay = <>
                            <div className="absolute left-[-6%] top-1/3 cursor-pointer" onClick={scrollLeft}>
                                <FontAwesomeIcon className="text-xl" style={arrowStyle} icon={faChevronLeft} />
                            </div>
                            <div className="absolute right-[-6%] top-1/3 cursor-pointer" onClick={scrollRight}>
                                <FontAwesomeIcon className="text-xl" style={arrowStyle} icon={faChevronRight} />
                            </div>
                        </>
    return (
        <div className="relative w-inherit">
            { screenSize === "Large" && scrollDisplay }
            <div ref={scrollContainerRef} className={ style }>
                { children }
            </div>
        </div>
    )
};



interface HorizontalScrollWithTopRightArrowsProps {
    children: ReactNode;
    arrowStyle?: CSSProperties;
  }
  
  export function HorizontalScrollWithTopRightArrows({ children, arrowStyle }: HorizontalScrollWithTopRightArrowsProps) {
    const { screenSize } = useScreenSize();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
  
    const scrollLeft = () => {
      scrollContainerRef.current?.scrollBy({ left: -300, behavior: "smooth" });
    };
  
    const scrollRight = () => {
      scrollContainerRef.current?.scrollBy({ left: 300, behavior: "smooth" });
    };
  
    const scrollStyle =
      screenSize === "Large" || "Small"
        ? "w-full grid grid-flow-col auto-cols-auto gap-8 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        : "flex flex-col items-center gap-8 py-10";
  
    return (
      <div className="relative w-full">
        {screenSize === "Large" && (
          <div className="flex justify-end px-4 mb-4">
            <div className="flex gap-2">
              <FontAwesomeIcon className="text-xl cursor-pointer" style={arrowStyle} icon={faChevronLeft} onClick={scrollLeft} />
              <FontAwesomeIcon className="text-xl cursor-pointer" style={arrowStyle} icon={faChevronRight} onClick={scrollRight} />
            </div>
          </div>
        )}
  
        <div ref={scrollContainerRef} className={scrollStyle}>
          {children}
        </div>
      </div>
    );
  }


export function Col2ContentFrame({ children } : FrameProps) {
    return (
        <div className="w-full lg:grid lg:grid-cols-2 lg:grid-rows-1 lg:items-center sm:flex sm:flex-col gap-12 mx-auto">
            { children }
        </div>
    )
};

export function Col3ContentFrame({ children } : FrameProps) {
    return (
        <div className="w-full lg:grid lg:grid-cols-3 lg:grid-rows-1 sm:flex sm:flex-col gap-8 mx-auto">
            { children }
        </div>
    )
};

export function Col4ContentFrame({ children } : FrameProps) {
    return (
        <div className="w-full lg:grid lg:grid-cols-4 lg:grid-rows-1 sm:flex sm:flex-col gap-8 mx-auto">
            { children }
        </div>
    )
};

interface ColAutoFrameProps extends FrameProps {
    spacing: number;
};
ColAutoFrame.defaultProps = {
    spacing: 24
};
export function ColAutoFrame({ children, spacing } : ColAutoFrameProps) {
    return (
        <div className="h-fit w-full lg:grid lg:grid-flow-col lg:auto-cols-auto sm:flex sm:flex-col" style={{ gap: spacing }}>
            { children }
        </div>
    )
};

interface SpacingFrameProps extends FrameProps {
    spacing: number;
    center?: boolean
};
SpacingFrame.defaultProps = {
    spacing: 48
};
export function SpacingFrame({ children, spacing, center } : SpacingFrameProps) {
    return (
        <div className={`h-fit flex flex-col ${center && "items-center justify-center"}`} style={{ gap: spacing }}>
            { children }
        </div>
    )
};

